import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, CircleNotch, Palette } from "@phosphor-icons/react";
import { toast } from "sonner";

// Reusable editor for the store's stone/enamel color palettes (owner-level, shared by all
// products). Used in the Logos page and in product creation so colors can be managed anywhere.
export default function PaletteEditor({ className = "" }) {
  const [palettes, setPalettes] = useState({ stones: [], enamels: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/palettes").then((r) => setPalettes(r.data)).catch(() => {}); }, []);
  const setColor = (grp, i, key, val) => setPalettes((p) => ({ ...p, [grp]: p[grp].map((c, j) => (j === i ? { ...c, [key]: val } : c)) }));
  const addColor = (grp) => setPalettes((p) => ({ ...p, [grp]: [...(p[grp] || []), { name: "Nuevo", hex: "#cccccc" }] }));
  const delColor = (grp, i) => setPalettes((p) => ({ ...p, [grp]: p[grp].filter((_, j) => j !== i) }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/palettes", palettes); setPalettes(data); toast.success("Colores guardados"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className={`border border-white/10 rounded-xl p-5 bg-[#121212] ${className}`} data-testid="product-palettes-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold flex items-center gap-2"><Palette size={18} className="text-gold" /> Colores de piedras y esmaltes</h3>
        <Button onClick={save} disabled={saving} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="product-save-palettes-btn">
          {saving ? <CircleNotch className="animate-spin" /> : "Guardar colores"}
        </Button>
      </div>
      <p className="text-xs text-neutral-500 mb-4">Estos colores aparecen en el personalizador del cliente (piedras y esmaltes). Aplican a todos tus productos.</p>
      <div className="grid md:grid-cols-2 gap-6">
        {[["stones", "Piedras"], ["enamels", "Esmaltes"]].map(([grp, label]) => (
          <div key={grp}>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm text-neutral-300">{label}</Label>
              <button onClick={() => addColor(grp)} className="text-xs text-gold flex items-center gap-1" data-testid={`product-add-${grp}`}><Plus size={12} /> Añadir</button>
            </div>
            <div className="space-y-2">
              {(palettes[grp] || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`product-${grp}-row-${i}`}>
                  <input type="color" value={c.hex} onChange={(e) => setColor(grp, i, "hex", e.target.value)} className="w-9 h-9 rounded border border-white/15 bg-transparent cursor-pointer" />
                  <Input value={c.name} onChange={(e) => setColor(grp, i, "name", e.target.value)} className="bg-white/5 border-white/15 h-9 flex-1" />
                  <button onClick={() => delColor(grp, i)} className="text-neutral-500 hover:text-red-400 p-1"><Trash size={15} /></button>
                </div>
              ))}
              {(palettes[grp] || []).length === 0 && <p className="text-xs text-neutral-600">Sin colores. Pulsa "Añadir".</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
